import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Brain, Coffee, Palette, Zap, Smile, Sparkles } from "lucide-react";
import api from "@/lib/api";

// Curated mood → effect tag mapping
const MOODS = [
  {
    key: "relaxed",
    label: "Unwind",
    sub: "Chill the day off",
    icon: <Coffee className="w-5 h-5" strokeWidth={1.6} />,
    color: "from-emerald-500/20 to-emerald-500/0",
  },
  {
    key: "sleepy",
    label: "Sleep",
    sub: "Drift off easy",
    icon: <Moon className="w-5 h-5" strokeWidth={1.6} />,
    color: "from-violet-500/20 to-violet-500/0",
  },
  {
    key: "focused",
    label: "Focus",
    sub: "Lock in & work",
    icon: <Brain className="w-5 h-5" strokeWidth={1.6} />,
    color: "from-amber-500/20 to-amber-500/0",
  },
  {
    key: "creative",
    label: "Create",
    sub: "Spark something new",
    icon: <Palette className="w-5 h-5" strokeWidth={1.6} />,
    color: "from-fuchsia-500/20 to-fuchsia-500/0",
  },
  {
    key: "energetic",
    label: "Energy",
    sub: "Move, hike, dance",
    icon: <Zap className="w-5 h-5" strokeWidth={1.6} />,
    color: "from-orange-500/20 to-orange-500/0",
  },
  {
    key: "happy",
    label: "Lift",
    sub: "Better the mood",
    icon: <Smile className="w-5 h-5" strokeWidth={1.6} />,
    color: "from-pink-500/20 to-pink-500/0",
  },
];

export const MoodFinder = () => {
  const navigate = useNavigate();
  const [available, setAvailable] = useState(new Set());

  useEffect(() => {
    api
      .get("/effects")
      .then((r) => setAvailable(new Set(r.data.effects.map((e) => e.toLowerCase()))))
      .catch(() => null);
  }, []);

  return (
    <section className="container-narrow mt-24">
      <div className="flex items-end justify-between mb-7">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-accent" strokeWidth={1.6} />{" "}
            What do you need today?
          </div>
          <h2 className="font-serif text-4xl">Search by feeling</h2>
          <p className="text-sm text-foreground/60 mt-2 max-w-md">
            Skip the strain encyclopedia. Pick a vibe — we'll surface what's in
            stock near you.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {MOODS.map((m) => {
          const disabled = available.size > 0 && !available.has(m.key);
          return (
            <button
              key={m.key}
              data-testid={`mood-${m.key}`}
              disabled={disabled}
              onClick={() => navigate(`/search?effect=${m.key}`)}
              className={`card-hover group relative overflow-hidden text-left rounded-2xl border border-border bg-card p-5 disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${m.color} opacity-0 group-hover:opacity-100 transition-opacity`}
              />
              <div className="relative z-10">
                <div className="w-10 h-10 rounded-xl bg-secondary text-foreground flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {m.icon}
                </div>
                <div className="font-serif text-xl">{m.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {m.sub}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};
